/**
 * Embeddings Service - Vector embeddings for semantic search
 * Supports OpenAI text-embedding-3-small or Voyage AI
 */

import type { Embedding, SemanticSearchResult } from './types';

// IndexedDB for caching embeddings locally
const DB_NAME = 'pkm-embeddings';
const DB_VERSION = 1;
const STORE_NAME = 'embeddings';

class EmbeddingsService {
  private apiKey: string | null = null;
  private provider: 'openai' | 'voyage' = 'openai';
  private model: string = 'text-embedding-3-small';
  private db: IDBDatabase | null = null;
  private dbReady: Promise<void>;

  constructor() {
    this.dbReady = this.initDB();
  }

  /**
   * Initialize IndexedDB for caching embeddings
   */
  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open embeddings database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'objectId' });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Initialize the service with API key
   */
  initialize(apiKey: string, provider: 'openai' | 'voyage' = 'openai'): void {
    this.apiKey = apiKey;
    this.provider = provider;
    this.model = provider === 'openai' ? 'text-embedding-3-small' : 'voyage-2';
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Generate embedding for text using OpenAI or Voyage
   */
  async generateEmbedding(text: string): Promise<number[]> {
    if (!this.apiKey) {
      throw new Error('Embeddings API key not configured');
    }

    // Truncate text if too long (8191 tokens for OpenAI)
    const truncatedText = text.substring(0, 8000);

    if (this.provider === 'openai') {
      return this.generateOpenAIEmbedding(truncatedText);
    } else {
      return this.generateVoyageEmbedding(truncatedText);
    }
  }

  /**
   * Generate embedding using OpenAI API
   */
  private async generateOpenAIEmbedding(text: string): Promise<number[]> {
    const response = await fetch('https://api.openai.com/v1/embeddings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: this.model,
        input: text,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'OpenAI API request failed');
    }

    const data = await response.json();
    return data.data[0].embedding;
  }

  /**
   * Generate embedding using Voyage AI API
   */
  private async generateVoyageEmbedding(text: string): Promise<number[]> {
    const response = await fetch('https://api.voyageai.com/v1/embeddings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: this.model,
        input: text,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Voyage AI API request failed');
    }

    const data = await response.json();
    return data.data[0].embedding;
  }

  /**
   * Store embedding in IndexedDB cache
   */
  async cacheEmbedding(objectId: string, vector: number[], text: string): Promise<void> {
    await this.dbReady;
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const embedding: Embedding = {
        objectId,
        vector,
        text,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const request = store.put(embedding);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cached embedding from IndexedDB
   */
  async getCachedEmbedding(objectId: string): Promise<Embedding | null> {
    await this.dbReady;
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(objectId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all cached embeddings
   */
  async getAllCachedEmbeddings(): Promise<Embedding[]> {
    await this.dbReady;
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete cached embedding
   */
  async deleteCachedEmbedding(objectId: string): Promise<void> {
    await this.dbReady;
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(objectId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cached embeddings
   */
  async clearCache(): Promise<void> {
    await this.dbReady;
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Calculate cosine similarity between two vectors
   */
  cosineSimilarity(a: number[], b: number[]): number {
    if (a.length !== b.length) return 0;

    let dotProduct = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }

    const magnitude = Math.sqrt(normA) * Math.sqrt(normB);
    return magnitude === 0 ? 0 : dotProduct / magnitude;
  }

  /**
   * Find similar objects using cached embeddings
   */
  async findSimilar(
    queryVector: number[],
    objects: Array<{ id: string; name: string; type: string }>,
    topK: number = 5
  ): Promise<SemanticSearchResult[]> {
    const embeddings = await this.getAllCachedEmbeddings();

    const scores: Array<{ objectId: string; score: number }> = [];

    for (const embedding of embeddings) {
      const score = this.cosineSimilarity(queryVector, embedding.vector);
      scores.push({ objectId: embedding.objectId, score });
    }

    // Sort by score descending
    scores.sort((a, b) => b.score - a.score);

    // Get top K results
    const topResults = scores.slice(0, topK);

    // Map to full results with object info
    return topResults
      .map(result => {
        const obj = objects.find(o => o.id === result.objectId);
        if (!obj) return null;
        return {
          objectId: obj.id,
          objectName: obj.name,
          objectType: obj.type,
          score: result.score,
        };
      })
      .filter((r): r is SemanticSearchResult => r !== null);
  }

  /**
   * Semantic search - find objects by meaning
   */
  async semanticSearch(
    query: string,
    objects: Array<{ id: string; name: string; type: string; content?: string }>,
    topK: number = 10
  ): Promise<SemanticSearchResult[]> {
    // Generate embedding for query
    const queryVector = await this.generateEmbedding(query);

    // Find similar objects
    const results = await this.findSimilar(queryVector, objects, topK);

    // Filter by minimum score
    return results.filter(r => r.score > 0.3);
  }

  /**
   * Get or create embedding for an object
   */
  async getOrCreateEmbedding(
    objectId: string,
    content: string,
    forceRefresh: boolean = false
  ): Promise<number[]> {
    if (!forceRefresh) {
      const cached = await this.getCachedEmbedding(objectId);
      if (cached) {
        return cached.vector;
      }
    }

    const vector = await this.generateEmbedding(content);
    await this.cacheEmbedding(objectId, vector, content.substring(0, 500));
    return vector;
  }

  /**
   * Batch generate embeddings for multiple objects
   */
  async batchGenerateEmbeddings(
    objects: Array<{ id: string; content: string }>,
    onProgress?: (completed: number, total: number) => void
  ): Promise<void> {
    const total = objects.length;
    let completed = 0;

    for (const obj of objects) {
      try {
        await this.getOrCreateEmbedding(obj.id, obj.content);
        completed++;
        onProgress?.(completed, total);
      } catch (error) {
        console.error(`Failed to generate embedding for ${obj.id}:`, error);
        completed++;
        onProgress?.(completed, total);
      }

      // Rate limiting: wait 100ms between requests
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

// Export singleton instance
export const embeddingsService = new EmbeddingsService();
